import { wrapIndex } from '../core/math';
import type { Track } from '../track/generate';
import type { Corner } from '../track/brakingzones';
import type { SpeedProfile } from '../track/speedprofile';

/** Sliding for less than this is a chirp, not a lock-up worth reporting. */
const LOCK_REPORT_METRES = 8;
/** Likewise for putting a wheel on the grass. */
const OFF_REPORT_METRES = 4;

export interface CornerDelta {
  number: number;
  /** Seconds lost against the ideal profile through this corner. */
  lost: number;
  /** Metres the player's braking point differed by. Negative is early. */
  brakeOffset: number;
  /** Player's minimum speed through the corner, m/s. */
  vMin: number;
  /** Ideal minimum speed, m/s. */
  vMinIdeal: number;
  lockedUp: boolean;
  wentOff: boolean;
}

/**
 * Compare a completed run against the solved profile, corner by corner.
 *
 * Once `vIdeal` exists this is close to free, and it is what converts a bare lap
 * time into something actionable: "T4, braked 18 m early, lost 0.4 s" tells a
 * player exactly what to do differently on the next attempt.
 */
export function analyseRun(
  track: Track,
  profile: SpeedProfile,
  corners: Corner[],
  speedAt: Float64Array,
  lockedAt: Uint8Array,
  offAt: Uint8Array,
): CornerDelta[] {
  const n = track.center.length;
  const ds = track.spacing;

  return corners.map((c) => {
    // Walk the corner from its ideal braking point through to the exit.
    const span: number[] = [];
    let i = c.brakeIndex;
    for (let guard = 0; guard < n; guard++) {
      span.push(i);
      if (i === c.exitIndex) break;
      i = wrapIndex(i + 1, n);
    }

    let tActual = 0;
    let tIdeal = 0;
    let vMin = Infinity;
    let vMinIdeal = Infinity;
    let lockedSamples = 0;
    let offSamples = 0;

    for (const idx of span) {
      const v = speedAt[idx];
      // Unvisited samples mean the kart cut this part of the track entirely.
      const vSafe = v > 0.1 ? v : 0.1;
      tActual += ds / vSafe;
      tIdeal += ds / Math.max(0.5, profile.vIdeal[idx]);
      if (v > 0.1) vMin = Math.min(vMin, v);
      vMinIdeal = Math.min(vMinIdeal, profile.vIdeal[idx]);
      if (lockedAt[idx]) lockedSamples++;
      if (offAt[idx]) offSamples++;
    }

    // Distance-based, not a boolean. A momentary chirp of the rears is not what
    // cost the lap, and flagging it as the headline buries the brake-point note
    // that actually tells the player what to change.
    const locked = lockedSamples * ds >= LOCK_REPORT_METRES;
    const off = offSamples * ds >= OFF_REPORT_METRES;

    // Where did the player actually start slowing? Their fastest point on the
    // approach is where braking began.
    //
    // Walking back from the apex while speed is falling — the way brakeIndex
    // itself is derived — cannot be used here: it assumes speed drops
    // monotonically all the way in, and a driver who brakes early then coasts
    // has a flat or rising patch that stops the walk short, which then reads as
    // braking dozens of metres *late*.
    const searchSteps = Math.round((c.brakeLength + 70) / ds);
    let playerBrake = c.apexIndex;
    let peakSpeed = -1;
    let scan = c.apexIndex;
    for (let s = 0; s < searchSteps; s++) {
      scan = wrapIndex(scan - 1, n);
      const v = speedAt[scan];
      if (v < 0.1) continue; // never visited; skip rather than give up
      if (v > peakSpeed) {
        peakSpeed = v;
        playerBrake = scan;
      }
    }
    let offsetSteps = wrapIndex(playerBrake - c.brakeIndex, n);
    if (offsetSteps > n / 2) offsetSteps -= n;

    return {
      number: c.number,
      lost: Math.max(0, tActual - tIdeal),
      brakeOffset: offsetSteps * ds,
      vMin: vMin === Infinity ? 0 : vMin,
      vMinIdeal,
      lockedUp: locked,
      wentOff: off,
    };
  });
}

/** Plain-language summary of the single corner that cost the most. */
export function describeWorst(d: CornerDelta): string {
  if (d.wentOff) return `T${d.number}: ran off the track`;
  if (d.lockedUp) return `T${d.number}: locked the rears`;
  if (d.brakeOffset < -6) {
    return `T${d.number}: braked ${Math.abs(Math.round(d.brakeOffset))}m early`;
  }
  if (d.brakeOffset > 6) return `T${d.number}: braked ${Math.round(d.brakeOffset)}m late`;
  const gap = (d.vMinIdeal - d.vMin) * 3.6;
  if (gap > 4) return `T${d.number}: ${Math.round(gap)} km/h slow at the apex`;
  return `T${d.number}: your weakest corner`;
}
